import { useEffect, useRef, useState } from "react";

const pricingData = [
  {
    plan: "２食付き",
    description: "夕食・朝食付きの基本プラン",
    adult: "9,000",
    child: "8,000",
    infant: "2,000",
    highlight: true,
  },
  {
    plan: "夕食付き",
    description: "夕食のみのプラン",
    adult: "8,000",
    child: "7,000",
    infant: "2,000",
    highlight: false,
  },
  {
    plan: "朝食付き",
    description: "朝食のみのプラン",
    adult: "8,000",
    child: "7,000",
    infant: "2,000",
    highlight: false,
  },
  {
    plan: "素泊まり",
    description: "お食事なしのシンプルプラン",
    adult: "6,000",
    child: "5,000",
    infant: "2,000",
    highlight: false,
  },
];

export default function PricingSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} id="pricing" className="py-20 md:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          className={`text-center mb-16 md:mb-20 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <p className="text-stone-500 text-xs tracking-[0.3em] mb-4">PRICING</p>
          <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mb-4">
            料金
          </h2>
          <p className="text-stone-500 text-sm md:text-base max-w-lg mx-auto mb-8">
            ご宿泊プランに応じた料金表です。全てお一人様あたりの税別価格となります。
          </p>
          {/* 受け入れ実績バッジ */}
          <div className="inline-flex items-center gap-3 px-7 py-3.5 bg-warm-500 text-white rounded-full ring-4 ring-warm-300/50 animate-pulse">
            <i className="ri-award-line text-xl md:text-2xl" />
            <div className="flex flex-col items-start leading-tight">
              <span className="text-base md:text-lg font-bold tracking-wider whitespace-nowrap">お仕事での長期滞在大歓迎</span>
              <span className="text-sm md:text-base whitespace-nowrap">（受け入れ実績多数あり）</span>
            </div>
            <i className="ri-check-double-line text-xl md:text-2xl" />
          </div>
        </div>

        {/* Pricing table */}
        <div
          className={`transition-all duration-700 delay-150 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {/* Desktop table */}
          <div className="hidden md:block bg-warm-50 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-warm-200">
                  <th className="text-left py-5 px-6 md:px-8 text-stone-500 text-xs tracking-[0.2em] font-normal">
                    プラン
                  </th>
                  <th className="text-center py-5 px-4 md:px-6 text-stone-500 text-xs tracking-[0.2em] font-normal">
                    大人
                    <span className="block text-[10px] tracking-normal text-stone-400 mt-0.5">
                      中学生以上
                    </span>
                  </th>
                  <th className="text-center py-5 px-4 md:px-6 text-stone-500 text-xs tracking-[0.2em] font-normal">
                    子ども
                    <span className="block text-[10px] tracking-normal text-stone-400 mt-0.5">
                      小学生
                    </span>
                  </th>
                  <th className="text-center py-5 px-4 md:px-6 text-stone-500 text-xs tracking-[0.2em] font-normal">
                    幼児
                    <span className="block text-[10px] tracking-normal text-stone-400 mt-0.5">
                      ３歳以上
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pricingData.map((item, index) => (
                  <tr
                    key={item.plan}
                    className={`border-b border-warm-100 last:border-b-0 transition-colors duration-200 ${
                      item.highlight ? "bg-warm-100/60" : "hover:bg-warm-50/80"
                    }`}
                  >
                    <td className="py-5 px-6 md:px-8">
                      <div className="flex items-center gap-3">
                        <span className="font-serif text-stone-900 text-base md:text-lg whitespace-nowrap">
                          {item.plan}
                        </span>
                        {item.highlight && (
                          <span className="text-[10px] tracking-wider px-2 py-0.5 rounded-full bg-warm-500/15 text-warm-700 whitespace-nowrap">
                            おすすめ
                          </span>
                        )}
                      </div>
                      <p className="text-stone-400 text-xs mt-1">{item.description}</p>
                    </td>
                    <td className="text-center py-5 px-4 md:px-6">
                      <span className="font-serif text-stone-900 text-lg md:text-xl">
                        {item.adult}
                      </span>
                      <span className="text-stone-500 text-xs ml-0.5">円</span>
                    </td>
                    <td className="text-center py-5 px-4 md:px-6">
                      <span className="font-serif text-stone-900 text-lg md:text-xl">
                        {item.child}
                      </span>
                      <span className="text-stone-500 text-xs ml-0.5">円</span>
                    </td>
                    <td className="text-center py-5 px-4 md:px-6">
                      <span className="font-serif text-stone-900 text-lg md:text-xl">
                        {item.infant}
                      </span>
                      <span className="text-stone-500 text-xs ml-0.5">円</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-4">
            {pricingData.map((item) => (
              <div
                key={item.plan}
                className={`rounded-2xl p-5 ${
                  item.highlight ? "bg-warm-100/60 ring-1 ring-warm-300/50" : "bg-warm-50"
                }`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className="font-serif text-stone-900 text-lg">{item.plan}</span>
                  {item.highlight && (
                    <span className="text-[10px] tracking-wider px-2 py-0.5 rounded-full bg-warm-500/15 text-warm-700 whitespace-nowrap">
                      おすすめ
                    </span>
                  )}
                </div>
                <div className="space-y-2.5">
                  <div className="flex justify-between items-center">
                    <span className="text-stone-500 text-sm">大人（中学生以上）</span>
                    <span className="font-serif text-stone-900 text-lg">
                      {item.adult}<span className="text-stone-500 text-xs ml-0.5">円</span>
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-stone-500 text-sm">子ども（小学生）</span>
                    <span className="font-serif text-stone-900 text-lg">
                      {item.child}<span className="text-stone-500 text-xs ml-0.5">円</span>
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-stone-500 text-sm">幼児（３歳以上）</span>
                    <span className="font-serif text-stone-900 text-lg">
                      {item.infant}<span className="text-stone-500 text-xs ml-0.5">円</span>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tax note */}
          <p className="text-center text-stone-400 text-xs mt-6 md:mt-8">
            ※ 表示価格は全て消費税・入湯税別となります
          </p>
        </div>
      </div>
    </section>
  );
}